import { Chart, ChartType, Plugin, TooltipModel } from "chart.js";
import { IWidgetSource } from "@main/reporting.v3/core/reporting-models";
import { UIChart } from "primeng/chart";

export type ChartTooltipFunction = (args: { chart: Chart; tooltip: TooltipModel<ChartType> }) => void;

export interface ChartSettings {
    // Type of the chart.
    type: ChartType;
    // Array of per-chart plugins to customize the chart behavior.
    plugins?: Plugin[];
    // Width of the chart.
    width?: string;
    // Height of the chart.
    height?: string;
    // Options to customize the chart.
    options?: Record<string, any>;
}

export enum ChartTypeEnum {
    Bar = "bar",
    Bubble = "bubble",
    Doughnut = "doughnut",
    Line = "line",
    Pie = "pie",
    PolarArea = "polarArea",
    Radar = "radar",
    Scatter = "scatter"
}

export const ChartTypeEnumLabel = new Map<ChartTypeEnum, string>([
    [ChartTypeEnum.Bar, "Bar chart"],
    [ChartTypeEnum.Bubble, "Bubble chart"],
    [ChartTypeEnum.Doughnut, "Doughnut chart"],
    [ChartTypeEnum.Line, "Line chart"],
    [ChartTypeEnum.Pie, "Pie chart"],
    [ChartTypeEnum.PolarArea, "Polar area chart"],
    [ChartTypeEnum.Radar, "Radar chart"],
    [ChartTypeEnum.Scatter, "Scatter chart"]
]);

export enum ChartSourceEnum {
    Single = "single",
    Multiple = "multiple"
}

export const ChartSourceEnumLabel = new Map<ChartSourceEnum, string>([
    [ChartSourceEnum.Single, "Single Series"],
    [ChartSourceEnum.Multiple, "Multiple Series"]
]);

export enum ColumnPurpose {
    AxisX = "axisX",
    AxisY = "axisY",
    Series = "series",
    BubbleSize = "bubbleSize",
    Tooltip = "tooltip",
}

export enum Refresh {
    Metadata = 1,
    Columns = 2,
    Options = 3,
    Data = 4,
    Settings = 5
}

export enum ChartAxis {
    X = "x",
    Y = "y",
    R = "r"
}

export interface ChartAxisSettings {
    name?: string;
    unit?: string;
    label?: string;
    min?: number;
    max?: number;
    type?: string;
    required: boolean;
    display: boolean;
    visible: () => boolean;
    numeric: () => boolean;
    defaultMin: () => number;
    defaultMax: () => number;
}

export interface ColumnSearchingOptions {
    purpose: ColumnPurpose;
    predicate: (alias: string) => boolean;
    exclude: string[];
}

export interface IChartBuildDTO {
    id: string;
    chartSettingsJson?: string;

    // Foreign key and navigational properties.
    querySourceId: string;
    widgetSourceId?: string;

    widgetSource?: IWidgetSource;

    columns: IChartBuildColumnDTO[];
}

export interface IChartBuildColumnDTO {
    id: string;
    queryAlias: string;
    columnName: string;
    chartAlias?: string;
    columnPurpose: ColumnPurpose;
}

export interface IChartViewDTO {
    id: string;
    chartSettingsJson?: string;

    // Foreign key and navigational properties.
    querySourceId?: string;
    widgetSourceId: string;

    widgetSource: IWidgetSource;

    columns: IChartViewColumnDTO[];

    // Non-database properties.
    queryVariables: string[];
}

export interface IChartViewColumnDTO {
    id: string;
    queryAlias: string;
    columnName: string;
    chartAlias?: string;
    columnPurpose: ColumnPurpose;

    // Foreign keys and navigational properties.
    chartId: string;
}

export interface IChartPdfTemplateSettings {
    template: string;
    chart: UIChart;
}